import React, { useState, useEffect, useRef, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Navbar from "./components/Navbar";
import Carousel from "./components/Carousel";
import IntroOverlay from "./components/IntroOverlay";
import "./index.css";
import "./components/resources/ResourceVisuals.css";
import backgroundLogo from "./assets/neuralward-logo-sm.svg";
import CircularBackgroundTransition from "./components/CircularBackgroundTransition";
import AnimatedTextSection from "./components/AnimatedTextSection";
import { UserProvider, useUser } from "./contexts/UserContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import NotificationSystem from "./components/notifications/NotificationSystem";
import connectionService from "./services/connectionService";
import { onFirstInteraction } from "./utils/firstInteraction";
import initTruConversion, { trackIdentity } from "./utils/truConversion";
import BannedScreen from "./components/BannedScreen";
import CookieBanner from "./components/legal/CookieBanner";
import RotatedSlider from "./components/RotatedSlider";
import AnimatedSectionContainer from "./components/AnimatedSectionContainer";
import AnimatedCircularProgress from "./components/AnimatedCircularProgress";
import GrowthBarGraph from "./components/GrowthBarGraph";
import ImageShowCase, { PulseEffect } from "./components/ImageShowCase";
import { AnimationErrorHandler } from "./components/AnimationFixes";
import SlidePerformance from "./components/slides/SlidePerformance";
import SlideOperations from "./components/slides/SlideOperations";
import SlideDefense from "./components/slides/SlideDefense";
import SlideAutonomous from "./components/slides/SlideAutonomous";
import SlideAdaptive from "./components/slides/SlideAdaptive";
import SlideDevTools from "./components/slides/SlideDevTools";
import LoadingSpinner from "./components/shared/LoadingSpinner";

// Lazy-load heavy components for performance
const AppRouter = React.lazy(() => import("./components/store/AppRouter"));
const AdminPanel = React.lazy(() => import("./components/admin/AdminPanel"));
const MyLicenses = React.lazy(() => import("./components/user/MyLicenses"));
const ProfileRoutes = React.lazy(
  () => import("./components/user/ProfileRoutes"),
);
const SolutionPage = React.lazy(
  () => import("./components/solutions/SolutionPage"),
);
const ProductLandingPage = React.lazy(
  () => import("./components/products/ProductLandingPage"),
);
const Tempbox = React.lazy(() => import("./components/temp/Tempbox"));
const PrivacyPolicy = React.lazy(
  () => import("./components/legal/PrivacyPolicy"),
);
const CookiePolicy = React.lazy(
  () => import("./components/legal/CookiePolicy"),
);
const RefundPolicy = React.lazy(
  () => import("./components/legal/RefundPolicy"),
);
const AiEthics = React.lazy(
  () => import("./components/legal/AiEthics"),
);
const TermsOfService = React.lazy(
  () => import("./components/legal/TermsOfService"),
);
const Accessibility = React.lazy(
  () => import("./components/legal/Accessibility"),
);
const Documentation = React.lazy(
  () => import("./components/resources/Documentation"),
);
const CaseStudies = React.lazy(
  () => import("./components/resources/CaseStudies"),
);
const Blog = React.lazy(() => import("./components/resources/Blog"));
const Webinars = React.lazy(() => import("./components/resources/Webinars"));
const AIReadinessAssessment = React.lazy(
  () => import("./components/resources/AIReadinessAssessment"),
);
const BlogPost = React.lazy(() => import("./components/resources/BlogPost"));
const DocArticle = React.lazy(
  () => import("./components/resources/DocArticle"),
);
const CaseStudyDetail = React.lazy(
  () => import("./components/resources/CaseStudyDetail"),
);
const AboutPage = React.lazy(() => import("./components/company/AboutPage"));
const CareersPage = React.lazy(() => import("./components/company/CareersPage"));
const CheckoutPage = React.lazy(() => import("./components/checkout/CheckoutPage"));
const AppAuthorizePage = React.lazy(() => import("./components/auth/AppAuthorizePage"));
const GetStartedPage = React.lazy(() => import("./components/getstarted/GetStartedPage"));
const ServicesFlow = React.lazy(() => import("./components/getstarted/ServicesFlow"));
const DeveloperFlow = React.lazy(() => import("./components/getstarted/DeveloperFlow"));
const DeveloperCenter = React.lazy(() => import("./components/developer/DeveloperCenter"));
const JoinOrgPage = React.lazy(() => import("./components/getstarted/JoinOrgPage"));
const GiftClaimPage = React.lazy(() => import("./components/gifts/GiftClaimPage"));
const Tutorials = React.lazy(() => import("./components/resources/Tutorials"));
const TutorialDetail = React.lazy(() => import("./components/resources/TutorialDetail"));
const KnowledgeBase = React.lazy(() => import("./components/support/KnowledgeBase"));
const CommunityForum = React.lazy(() => import("./components/support/CommunityForum"));
const TechnicalSupport = React.lazy(() => import("./components/support/TechnicalSupport"));
const StatusPage = React.lazy(() => import("./components/support/StatusPage"));
const WhitePapers = React.lazy(() => import("./components/insights/WhitePapers"));
const IndustryReports = React.lazy(() => import("./components/insights/IndustryReports"));
const ROICalculator = React.lazy(() => import("./components/insights/ROICalculator"));

// NEW: Lazy-load UI sections
const Footer = React.lazy(() => import("./components/Footer"));
const NeuralAssistant = React.lazy(
  () => import("./components/chat/NeuralAssistant"),
);
const AppCenter = React.lazy(() => import("./components/AppCenter"));
const ServiceCards = React.lazy(() => import("./components/ServiceCards"));
const MetricsShowcase = React.lazy(
  () => import("./components/MetricsShowcase"),
);
const FeatureShowcase = React.lazy(
  () => import("./components/FeatureShowcase"),
);

// Lazy-load demo and payment components
const MetricsThemeDemo = React.lazy(
  () => import("./components/metrics/MetricsThemeDemo"),
);
const BubbleExample = React.lazy(
  () => import("./components/metrics/BubbleExample"),
);
const PayPalSuccess = React.lazy(
  () => import("./components/store/components/payment/PayPalSuccess"),
);
const PayPalCancel = React.lazy(
  () => import("./components/store/components/payment/PayPalCancel"),
);
const MercadoPagoSuccess = React.lazy(
  () => import("./components/store/components/payment/MercadoPagoSuccess"),
);
const MercadoPagoFailure = React.lazy(
  () => import("./components/store/components/payment/MercadoPagoFailure"),
);
const MercadoPagoPending = React.lazy(
  () => import("./components/store/components/payment/MercadoPagoPending"),
);

// Standalone Chat component — full-page, no navbar/footer
const StandaloneChatPage = () => (
  <div
    style={{
      width: "100vw",
      height: "100vh",
      background: "rgba(28,28,28,0.98)",
      overflow: "hidden",
    }}
  >
    <NeuralAssistant standalone />
  </div>
);

// Standalone Metrics Demo component - no navbar or footer
const StandaloneMetricsDemo = () => {
  return (
    <div className="bg-gray-50 min-h-screen">
      <MetricsThemeDemo />
    </div>
  );
};

// Standalone Bubbles Demo component - no navbar or footer
const StandaloneBubblesDemo = () => {
  return (
    <div className="bg-gray-50 min-h-screen">
      <BubbleExample />
    </div>
  );
};

// Company Overview section - module-level to prevent remounts on AppContent re-renders
const CompanyOverview = () => {
  const overviewRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startY = 400;
  const endY = viewportWidth < 768 ? 140 : 230;
  const horizontalOffset = 0;
  const logoHeight = viewportWidth < 768 ? 250 : 400;

  const { scrollYProgress } = useScroll({
    target: overviewRef,
    offset: ["start end", "start center"],
  });

  const logoY = useTransform(scrollYProgress, [0, 0.5], [startY, endY]);

  return (
    <div
      ref={overviewRef}
      className="relative"
      style={{
        marginTop: viewportWidth < 768 ? "10rem" : "14rem",
        position: "relative",
      }}
      id="overview-container"
    >
      <motion.div
        className="absolute w-full"
        style={{ bottom: "100%", y: logoY }}
      >
        <div
          className="relative w-full flex justify-center"
          style={{ transform: `translateX(${horizontalOffset}px)` }}
        >
          <img
            src={backgroundLogo}
            alt="Neuralward Logo"
            width={Math.round(logoHeight * 0.836)}
            height={logoHeight}
            loading="eager"
            style={{
              height: `${logoHeight}px`,
              aspectRatio: "156.2 / 186.8",
            }}
          />
        </div>
      </motion.div>

      <CircularBackgroundTransition bgColor="#1e1e21" textColor="#ffffff">
        <div id="overview" className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col items-center justify-center gap-12 py-12">
            <h2 className="text-3xl md:text-4xl font-bold feature-highlight">
              Company Overview
            </h2>
            <div className="text-xl md:text-2xl leading-relaxed max-w-6xl text-center">
              Neuralward develops and distributes advanced systems across
              various industries, integrating neural networks and AI-driven
              capabilities where they enhance efficiency and streamline
              decision-making. The mission is to deliver high-performance,
              intelligent, and scalable technologies for modern businesses and
              consumers.
            </div>
            <div className="text-xl md:text-2xl leading-relaxed max-w-6xl text-center">
              Focused on innovation, flexibility, and real-world impact,
              Neuralward bridges the gap between AI-powered advancements and
              practical, user-friendly solutions.
            </div>
          </div>
        </div>
      </CircularBackgroundTransition>
    </div>
  );
};

// Features section - module-level to prevent remounts
// RotatedSlider mounts eagerly so its ~400px of height is reserved up front —
// mounting it mid-scroll shifted everything below it (scroll-anchor jumps).
// Its own useInView still delays the entry animation until it's on screen.
const Features = () => {
  return (
    <div id="features">
      <FeatureShowcase />
      <AnimatedSectionContainer>
        <AnimatedTextSection
          text="Picture a world where intelligence flows seamlessly between human and machine. At Neuralward, we transform this vision into reality, creating solutions that elevate what's humanly possible."
          highlightWords={["Neuralward", "vision", "reality"]}
        />
        <div style={{ width: "100%" }}>
          <RotatedSlider key="slider-one-time-mount">
              <AnimatedCircularProgress progress={75} label="Innovation" />
              <div className="relative inline-block">
                <ImageShowCase
                  imageSrc="./sections_img/02.webp"
                  altText="Powered with AI Technology"
                  chipText="AI Powered"
                  withEffect={PulseEffect}
                  bgColor="#163241"
                  mixBlendModes={["overlay", "overlay"]}
                />
              </div>
              <GrowthBarGraph />
          </RotatedSlider>
        </div>
      </AnimatedSectionContainer>
    </div>
  );
};

// Future section - module-level to prevent remounts
const Future = () => <MetricsShowcase />;

// Main wrapped component with access to router hooks
function AppContent() {
  const { isProcessingOAuth, user, userData } = useUser();
  const location = useLocation();

  // Ties recordings/heatmaps to the signed-in account. Queues harmlessly if the
  // TruConversion script hasn't loaded yet — it drains _tip on load.
  useEffect(() => {
    trackIdentity(user?.email || user?.uid);
  }, [user]);

  // Feeds the admin visitor map's per-IP journey. The router is the source of
  // truth for route changes, so no history monkey-patching is needed; the call
  // no-ops until connectionService has a visit row (i.e. after first gesture).
  useEffect(() => {
    connectionService.notePage(location.pathname);
  }, [location.pathname]);

  // All hooks must be declared before any early return
  const [heroHeight, setHeroHeight] = useState(window.innerHeight);
  const [ipBanned, setIpBanned] = useState(false);

  const [showNavbar, setShowNavbar] = useState(
    () => !!window.__nw_introDone || window.location.pathname !== "/",
  );
  const [allowScrolling, setAllowScrolling] = useState(
    () => !!window.__nw_introDone || window.location.pathname !== "/",
  );
  const [introJustFinished, setIntroJustFinished] = useState(false);
  // Tracks whether the intro animation has completed — drives both the UI
  // unlock AND the Carousel's slide-0-to-1 transition.
  const [introDone, setIntroDone] = useState(
    () => !!window.__nw_introDone || window.location.pathname !== "/",
  );

  // Automatically reset the intro just finished state after the expected animation duration
  useEffect(() => {
    if (introJustFinished) {
      const timer = setTimeout(() => {
        setIntroJustFinished(false);
      }, 1500); // 1.5s is plenty for the spring animation to complete
      return () => clearTimeout(timer);
    }
  }, [introJustFinished]);

  // ────────────────────────────────────────────────────────────────────────
  // Intro completion: single stable timer, independent of IntroOverlay's lifecycle.
  //
  // The IntroOverlay CSS animation timeline:
  //   • SVG stroke-draw ............. 0 → 1.8s
  //   • Fill fade-in ................ 1.3s → 1.7s
  //   • Logo resize + text expand ... 1.4s → 2.2s
  //
  // We fire at 2500ms (2.2s last keyframe + 300ms cushion) so the user
  // sees the full animation before anything else appears.
  // ────────────────────────────────────────────────────────────────────────
  const introFiredRef = useRef(false);
  useEffect(() => {
    // Already done (revisit, HMR, non-root path).
    if (window.__nw_introDone || window.location.pathname !== "/") {
      introFiredRef.current = true;
      return;
    }

    const timer = setTimeout(() => {
      // Guard: only fire once even if StrictMode double-mounts.
      if (introFiredRef.current) return;
      introFiredRef.current = true;

      window.__nw_introDone = true;
      window.dispatchEvent(new CustomEvent("nw:introDone"));
      setIntroJustFinished(true);
      setShowNavbar(true);
      setAllowScrolling(true);
      setIntroDone(true);
    }, 2500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: if we entered on a non-root path, skip intro
  useEffect(() => {
    if (window.location.pathname !== "/" && !window.__nw_introDone) {
      window.__nw_introDone = true;
      window.dispatchEvent(new CustomEvent("nw:introDone"));
      setShowNavbar(true);
      setAllowScrolling(true);
      setIntroDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to disable/enable scrolling
  useEffect(() => {
    // Function to disable scrolling
    const disableScroll = () => {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Apply styles to prevent scrolling
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    };

    // Function to enable scrolling
    const enableScroll = () => {
      // Remove the fixed position
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.style.overflow = "";
      document.body.style.touchAction = "";

      // Restore the scroll position
      const scrollY = document.body.style.top;
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    };

    // Apply scroll behavior based on state
    if (!allowScrolling) {
      disableScroll();
    } else {
      enableScroll();
    }

    // Clean up function
    return () => {
      // Make sure scrolling is enabled when component unmounts
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [allowScrolling]);

  // NEW: Initialize connection service - runs once on mount
  useEffect(() => {
    // Deferred to the first user gesture: opening the RTDB presence connection
    // at boot put firebaseio sync-XHR deprecations + throttled-connection
    // errors in every Lighthouse trace (Best Practices 77). Bots never gesture.
    onFirstInteraction(() => connectionService.initialize());

    // Account-less ban enforcement. connectionService fires this only if the
    // visitor's own IP is on the admin ban list; the lookup rides along with
    // the IP it already resolves, so nothing extra happens before a gesture.
    const unsubBan = connectionService.onIpBanned(() => setIpBanned(true));

    // TruConversion (heatmaps / recordings / funnels) — also gesture-gated.
    initTruConversion();

    // Cleanup on unmount
    return () => {
      unsubBan();
      connectionService.cleanup();
    };
  }, []); // Empty dependency array - run once on mount

  // NEW: Connection monitoring effect
  useEffect(() => {
    const unsubscribe = connectionService.addConnectionListener(
      (connectionInfo) => {
        console.log("Connection status changed:", connectionInfo);

        if (connectionInfo.type === "browser") {
          if (connectionInfo.connected) {
            console.log("✅ Back online - Firebase services restored");
            // You could add a success notification here using your notification system
          } else {
            console.log("⚠️ Gone offline - Using cached data");
            // You could add an offline notification here using your notification system
          }
        }
      },
    );

    console.log(
      "Initial connection status:",
      connectionService.getConnectionStatus(),
    );

    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array

  // Content slides (always present)
  const contentSlides = [
    {
      id: 1,
      content: <SlidePerformance />,
      preloadImages: ["./carousel_img/performancebg.webp", "./carousel_img/performance.webp"],
    },
    {
      id: 2,
      content: <SlideOperations />,
      preloadImages: ["./carousel_img/operationsbg.webp", "./carousel_img/operationsbg2.webp"],
    },
    {
      id: 3,
      content: <SlideDevTools />,
      preloadImages: ["./carousel_img/devtoolsbg.webp", "./carousel_img/devtools.webp"],
    },
    {
      id: 4,
      content: <SlideDefense />,
      preloadImages: ["./carousel_img/defensebg.webp", "./carousel_img/defensebg2.webp", "./carousel_img/shield.webp"],
    },
    {
      id: 5,
      content: <SlideAutonomous />,
      preloadImages: ["./carousel_img/autonomous_bg.webp", "./carousel_img/autonomous.webp"],
    },
    {
      id: 6,
      content: <SlideAdaptive />,
      preloadImages: ["./carousel_img/adaptive.webp"],
    },
  ];

  // Always include ALL slides — the Carousel handles skipping the intro via skipIntro prop
  const slides = [
    {
      id: 0,
      label: "",
      title: "",
      description: "",
      content: <IntroOverlay />,
      isCustomContent: true,
    },
    ...contentSlides,
  ];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setHeroHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Scroll to the section matching the router location — covers both direct
  // loads of /features etc. AND arriving from a subpage via a navbar section
  // click (which does a real navigate()). Uses the ROUTER location on purpose:
  // the scroll spy's replaceState URL sync never touches it, so in-page
  // scrolling can't retrigger this, and the spy resetting the URL bar to "/"
  // on mount can't clobber the target before we read it.
  useEffect(() => {
    const sectionByPath = {
      "/overview": "overview",
      "/services": "services",
      "/features": "features",
      "/future": "future",
      "/appcenter": "appcenter",
    };
    const sectionId = sectionByPath[location.pathname];
    if (!sectionId) return;

    // Use a consistent offset for each section
    const sectionOffsets = {
      overview: 96 + 20, // navbar + buffer
      services: 64 + 20,
      features: 64 + 20,
      future: 64 + 20,
      appcenter: 64 + 20,
    };

    // The home sections are lazy-loaded, so the target may not exist yet —
    // poll until it appears instead of gambling on a fixed delay.
    let cancelled = false;
    const deadline = Date.now() + 6000;
    const tryScroll = () => {
      if (cancelled) return;
      const section = document.getElementById(sectionId);
      if (!section) {
        if (Date.now() < deadline) setTimeout(tryScroll, 120);
        return;
      }
      const sectionPosition =
        section.getBoundingClientRect().top + window.pageYOffset;
      const offset = sectionOffsets[sectionId] || 84; // 64 + 20 default
      window.scrollTo({
        top: sectionPosition - offset,
        behavior: "smooth",
      });
    };
    const starter = setTimeout(tryScroll, 100);

    return () => {
      cancelled = true;
      clearTimeout(starter);
    };
  }, [location.pathname]);

  // Account-less ban. Client-side only, so treat it as a deterrent rather than
  // a security boundary — the enforcement that actually matters for flooding
  // lives server-side in the n8n chat Flood Gate.
  // An admin is never locked out by an IP ban. Bans target account-less
  // visitors, and without this a mis-aimed ban on a shared or recycled address
  // would leave nobody able to reach the admin panel to lift it.
  if (ipBanned && !userData?.permissions?.isAdmin) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "#0a0a12",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "2rem",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: ".75rem" }}>
          Access restricted
        </h1>
        <p style={{ color: "rgba(255,255,255,.6)", maxWidth: "28rem", lineHeight: 1.6 }}>
          Your connection has been blocked from this portal. If you believe this
          is a mistake, contact{" "}
          <a href="mailto:support@neuralward.com" style={{ color: "#f472b6" }}>
            support@neuralward.com
          </a>
          .
        </p>
      </div>
    );
  }

  // While Google OAuth code exchange is in flight, show the branded loading screen.
  if (isProcessingOAuth) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
        {/* Animated logo fills the entire background */}
        <IntroOverlay />

        {/* Pill slides up from below after the logo animation finishes (2.3s) */}
        <style>{`
          @keyframes nw-spin { to { transform: rotate(360deg); } }
          @keyframes nw-pill-up {
            0%   { transform: translateY(40px); opacity: 0; }
            100% { transform: translateY(0);    opacity: 1; }
          }
        `}</style>
        <div
          style={{
            position: "absolute",
            bottom: "10%",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            /* start invisible, then animate in after logo animation completes */
            opacity: 0,
            animation: "nw-pill-up 0.5s ease-out 2.3s forwards",
          }}
        >
          <LoadingSpinner text="Signing you in…" theme="dark" />
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Standalone Chat Route */}
        <Route path="/chat" element={<StandaloneChatPage />} />

        {/* Standalone Metrics Demo Route */}
        <Route path="/metrics-demo" element={<StandaloneMetricsDemo />} />

        {/* Standalone Bubbles Demo Route */}
        <Route path="/bubble-demo" element={<StandaloneBubblesDemo />} />

        {/* Banned User Route */}
        <Route path="/banned" element={<BannedScreen />} />

        {/* Admin Panel Route */}
        <Route path="/admin" element={<AdminPanel />} />

        {/* PayPal Payment Routes */}
        <Route path="/payment/paypal-success" element={<PayPalSuccess />} />
        <Route path="/payment/paypal-cancel" element={<PayPalCancel />} />

        {/* MercadoPago Payment Routes */}
        <Route
          path="/payment/mercadopago-success"
          element={<MercadoPagoSuccess />}
        />
        <Route
          path="/payment/mercadopago-failure"
          element={<MercadoPagoFailure />}
        />
        <Route
          path="/payment/mercadopago-pending"
          element={<MercadoPagoPending />}
        />

        {/* App Center Store Route - This now uses AppRouter which handles nested routing */}
        <Route path="/appcenter-store/*" element={<AppRouter />} />

        {/* Checkout */}
        <Route path="/checkout/:appId" element={<CheckoutPage />} />

        {/* Desktop app OAuth authorization */}
        <Route path="/auth/app-login" element={<AppAuthorizePage />} />

        {/* Gift claim page */}
        <Route path="/gift/:giftId" element={<GiftClaimPage />} />

        {/* Licenses */}
        <Route path="/my-licenses" element={<MyLicenses />} />

        {/* Profile */}
        <Route path="/profile/*" element={<ProfileRoutes />} />

        {/* Temp sandbox */}
        <Route
          path="/tempbox"
          element={
            <div
              style={{
                padding: "40px",
                background: "#171715",
                minHeight: "100vh",
              }}
            >
              <Tempbox />
            </div>
          }
        />

        {/* Solution pages — must be before the catch-all */}
        <Route path="/solutions/:slug" element={<SolutionPage />} />
        <Route path="/industries/:slug" element={<SolutionPage />} />
        <Route path="/deployment/:slug" element={<SolutionPage />} />

        {/* Product landing pages */}
        <Route path="/products/:slug" element={<ProductLandingPage />} />

        {/* Resource pages */}
        <Route path="/resources/docs" element={<Documentation />} />
        <Route path="/resources/docs/:id" element={<DocArticle />} />
        <Route path="/resources/blog" element={<Blog />} />
        <Route path="/resources/blog/:id" element={<BlogPost />} />
        <Route path="/resources/webinars" element={<Webinars />} />
        <Route path="/insights/case-studies" element={<CaseStudies />} />
        <Route
          path="/insights/case-studies/:id"
          element={<CaseStudyDetail />}
        />
        <Route
          path="/insights/ai-readiness"
          element={<AIReadinessAssessment />}
        />
        <Route path="/resources/tutorials" element={<Tutorials />} />
        <Route path="/resources/tutorials/:slug" element={<TutorialDetail />} />
        <Route path="/support/knowledge-base" element={<KnowledgeBase />} />
        <Route path="/support/forum" element={<CommunityForum />} />
        <Route path="/support/technical" element={<TechnicalSupport />} />
        <Route path="/support/status" element={<StatusPage />} />
        <Route path="/insights/white-papers" element={<WhitePapers />} />
        <Route path="/insights/reports" element={<IndustryReports />} />
        <Route path="/insights/roi-calculator" element={<ROICalculator />} />

        {/* Legacy CTA target (Webinars "Reserve My Seat") — route to the services flow */}
        <Route path="/demo" element={<Navigate to="/get-started/services" replace />} />

        {/* Get Started flow */}
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route path="/get-started/services" element={<ServicesFlow />} />
        <Route path="/get-started/developer" element={<DeveloperFlow />} />

        {/* Developer Center */}
        <Route path="/developers" element={<DeveloperCenter />} />

        {/* Organization invite link */}
        <Route path="/join/:token" element={<JoinOrgPage />} />

        {/* Company pages */}
        <Route path="/company/about" element={<AboutPage />} />
        <Route path="/company/careers" element={<CareersPage />} />

        {/* Legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/ai-ethics" element={<AiEthics />} />
        <Route path="/legal" element={<TermsOfService />} />
        <Route path="/accessibility" element={<Accessibility />} />

        {/* Main Layout with Navbar and Footer for all other routes */}
        <Route
          path="*"
          element={
            <div className="font-sans bg-gray-50 min-h-screen relative overflow-x-hidden">
              {/* Conditional rendering of navbar with improved animation */}
              {showNavbar && (
                <motion.div
                  initial={{ y: introJustFinished ? -100 : 0 }}
                  animate={{ y: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                    mass: 1,
                  }}
                  className="fixed w-full"
                  style={{ zIndex: 101 }}
                >
                  <Navbar />
                </motion.div>
              )}

              <main className="pt-18 relative overflow-x-hidden">
                {/* Visually hidden H1 for SEO crawler consistency */}
                <h1 className="sr-only">
                  Neuralward | AI-Powered Enterprise Solutions & Machine
                  Intelligence Architecture
                </h1>
                {/* Carousel container with fixed height */}
                <div
                  className="relative w-full overflow-hidden"
                  style={{
                    height: `${heroHeight}px`,
                    minHeight: "300px",
                  }}
                >
                  {/* Dark background */}
                  <div className="absolute w-full h-full bg-[#1e1e21] overflow-hidden"></div>

                  {/* Carousel */}
                  <div className="absolute top-0 left-0 w-full h-full">
                    <motion.div
                      initial={{ opacity: 1, y: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="w-full h-full"
                      key="persistent-carousel"
                      style={{ x: 0, y: 0 }}
                    >
                      <Carousel
                        slides={slides}
                        heroHeight={heroHeight}
                        initialSlide={0}
                        skipIntro={introDone}
                        introComplete={introDone}
                      />
                    </motion.div>
                  </div>
                </div>

                {/* All main content sections */}
                <CompanyOverview />
                <Suspense fallback={<div className="h-96 bg-[#1e1e21]/50" />}>
                  <ServiceCards />
                  <Features />
                  <Future />
                  <AppCenter />
                </Suspense>

                {/* Nested routes for section navigation */}
                <Routes>
                  <Route path="/" element={null} />
                  <Route path="/overview" element={null} />
                  <Route path="/services" element={null} />
                  <Route path="/features" element={null} />
                  <Route path="/future" element={null} />
                  <Route path="/appcenter" element={null} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                {/* NOTE: /products/:slug, /solutions/:slug, etc. are handled by outer Routes above this catch-all */}
              </main>
              <Suspense fallback={<div className="h-64 bg-[#111113]" />}>
                <Footer />
              </Suspense>
              <CookieBanner isIntroFinished={allowScrolling} />
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
}

// Component to handle auto-scrolling to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPath = useRef(pathname);

  useEffect(() => {
    const homePaths = [
      "/",
      "/overview",
      "/services",
      "/features",
      "/future",
      "/appcenter",
    ];
    // Do not scroll to top if we are just switching between the home page's internal scroll spy sections
    const isInternalHomeNav =
      homePaths.includes(prevPath.current) && homePaths.includes(pathname);

    if (!isInternalHomeNav) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }

    prevPath.current = pathname;
  }, [pathname]);

  return null;
}

// Main App component that wraps the content with Router
export default function App() {
  return (
    <UserProvider>
      <NotificationProvider>
        <AnimationErrorHandler>
          <Router>
            <ScrollToTop />
            <NotificationSystem />
            <Suspense fallback={null}>
              <NeuralAssistant />
            </Suspense>
            <AppContent />
          </Router>
        </AnimationErrorHandler>
      </NotificationProvider>
    </UserProvider>
  );
}
